// [목적] "내 폴리오로 가져오기" 모달 — GET /folios?author=me 목록에서 대상 폴리오 선택 → day_import fork
// [이유] FlowLine A8(4c112997): 폴리오 보기(비소유자·로그인)의 Day 메뉴에서 트리거.
//        내 폴리오 목록은 useFolioFeed({author:'me'}) 재사용(MyFoliosPage/HomePage 와 동일 계약, 신규 훅 불필요).
// [사이드 이팩트] 선택 즉시 useDayImportFolio 호출 → 성공 시 대상 폴리오로 navigate + 토스트.
// [수정 시 고려사항] 소스 폴리오 자신은 목록에 노출돼도 무방(자기 자신을 고르면 BE 가 별도 에러를 내지 않고
//                   같은 폴리오에 Day 를 추가하게 되는데, 이는 folio.DAY_IMPORT_CONFLICT 는 아니고
//                   day_index 충돌 가능성만 있음 — 호출부가 소스 폴리오는 목록에서 제외해 방지).

import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';

import { ApiError } from '../../api/http.js';
import { useFolioFeed } from '../../hooks/useFolioFeed.js';
import { useDayImportFolio } from '../../hooks/useDayImportFolio.js';
import { useToast } from '../feedback/Toast.js';

const c = tokens.color;
const L = 'light' as const;

export interface DayImportDialogProps {
  open: boolean;
  onClose: () => void;
  sourceFolioId: string;
  sourceDayIndex: number;
}

/**
 * [목적] ApiError.code → 안내 문구 매핑
 * [이유] folio.DAY_IMPORT_CONFLICT(409) 는 forks_unique 위반(동일 Day 중복 임포트) — BE codes.ts 정본.
 * [사이드 이팩트] 없음(순수 함수)
 * [수정 시 고려사항] 코드 추가 시 이 맵만 갱신.
 */
function mapImportError(err: unknown): string {
  if (err instanceof ApiError && err.code === 'folio.DAY_IMPORT_CONFLICT') {
    return '이미 이 폴리오로 가져온 Day예요.';
  }
  return 'Day 가져오기에 실패했어요. 잠시 후 다시 시도해 주세요.';
}

/**
 * [목적] 내 폴리오 선택 모달 본체
 * [이유] A8 — 목록/로딩/에러/빈 상태 4분기 + 선택 시 mutation.
 * [사이드 이팩트] navigate('/folio/:id')로 성공 후 대상 폴리오 화면 진입.
 * [수정 시 고려사항] sourceFolioId 를 목록에서 제외 — 자기 자신에게 임포트하는 혼란 방지.
 */
export function DayImportDialog({
  open,
  onClose,
  sourceFolioId,
  sourceDayIndex,
}: DayImportDialogProps): JSX.Element {
  const navigate = useNavigate();
  const { show } = useToast();
  const { allFolios, isLoading, error } = useFolioFeed({ author: 'me', enabled: open, limit: 30 });
  const importMutation = useDayImportFolio();

  const candidates = allFolios.filter((f) => f.id !== sourceFolioId);

  function handleSelect(targetFolioId: string): void {
    importMutation.mutate(
      { sourceFolioId, sourceDayIndex, targetFolioId },
      {
        onSuccess: (folio) => {
          onClose();
          show('Day를 내 폴리오로 가져왔어요.', 'success');
          navigate(`/folio/${folio.id}`);
        },
        onError: (err) => show(mapImportError(err), 'error'),
      },
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="day-import-title"
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle
        id="day-import-title"
        sx={{ fontSize: 16, fontWeight: 700, color: c.ink.high[L] }}
      >
        내 폴리오로 가져오기
      </DialogTitle>
      <DialogContent>
        {isLoading && <CircularProgress size={20} aria-label="내 폴리오 불러오는 중" />}
        {!isLoading && Boolean(error) && (
          <Alert severity="error" role="alert">
            폴리오 목록을 불러오지 못했어요.
          </Alert>
        )}
        {!isLoading && !error && candidates.length === 0 && (
          <Typography sx={{ fontSize: 13, color: c.ink.mid[L] }}>
            가져올 폴리오가 없어요. 먼저 폴리오를 만들어 보세요.
          </Typography>
        )}
        {!isLoading && !error && candidates.length > 0 && (
          <List aria-label="내 폴리오 목록">
            {candidates.map((f) => (
              <ListItemButton
                key={f.id}
                onClick={() => handleSelect(f.id)}
                disabled={importMutation.isPending}
                aria-label={`${f.title}(으)로 가져오기`}
              >
                <ListItemText primary={f.title} secondary={f.subtitle ?? undefined} />
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ px: `${tokens.spacing[4]}px`, pb: `${tokens.spacing[3]}px` }}>
        <Button onClick={onClose} aria-label="취소" disabled={importMutation.isPending}>
          취소
        </Button>
      </DialogActions>
    </Dialog>
  );
}
